import { useRef, useState, useCallback, useEffect, type ElementRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import config from 'config';

type DownloadListItemProps = {
  url: string;
  onComplete: VoidFunction;
  fileName?: string;
};

interface BaseReportState {
  state?: string;
  data?: unknown;
}

interface DefaultState extends BaseReportState {
  state: 'default';
  data?: null;
}

interface LoadingState extends BaseReportState {
  state: 'loading';
  data?: null;
}

interface ErrorState extends BaseReportState {
  state: 'error';
  data: Error;
}

interface SuccessState extends BaseReportState {
  state: 'success';
  data: string;
}

type ReportState = DefaultState | LoadingState | ErrorState | SuccessState;

const DownloadListItem = ({ url, fileName = 'report.csv', onComplete }: DownloadListItemProps) => {
  const isCSV = fileName.includes('.csv');
  const download = useRef<ElementRef<'a'>>(null);
  const [{ state, data }, setData] = useState<ReportState>({ state: 'default' });

  const onFetch = useCallback(async () => {
    setData({ state: 'loading', data: null });

    try {
      const response = await fetch(url);
      const responseText = await response.text();

      setData({ state: 'success', data: responseText });
    } catch (err) {
      if (config('/debug')) console.error(err);
      setData({ state: 'error', data: err });
    } finally {
      setTimeout(() => onComplete(), 2500);
    }
  }, [url, onComplete]);

  useEffect(() => {
    if (state === 'default') {
      onFetch();
      return;
    }

    if (state === 'success') {
      download.current.click();
    }
  }, [state, onFetch]);

  return (
    <div className="bg-gray-800 p-3 rounded-md shadow-lg text-white">
      {state === 'loading' && (
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faSpinner} className="text-green-500" size="1x" spin />
          <p>Generating Your Download...</p>
        </div>
      )}
      {state === 'success' && (
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" size="1x" />
          <p>Download Complete</p>
        </div>
      )}
      {state === 'error' && (
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCircleExclamation} className="text-red-500" size="1x" />
          <p>There was an issue generating your report. Try again later.</p>
        </div>
      )}
      <a
        href={
          isCSV
            ? `data:text/csv;charset=utf-8,${encodeURIComponent(state === 'success' ? data : '')}`
            : url
        }
        download={fileName}
        ref={download}
        className="hidden"
      >
        Click to download
      </a>
    </div>
  );
};

export default DownloadListItem;
